import type { InputProps } from '../TextInput';
import type { InputHTMLAttributes, Ref } from 'react';
import type { FieldError } from 'react-hook-form';

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import { useClickAway, useDebounce } from 'react-use';

import ErrorMessage from '../../ErrorMessage';
import TextInput from '../TextInput';
import MessageContainer from '../TextInput/MessageContainer';

import BackgroundBar from './BackgroundBar';
import SliderContainer from './SliderContainer';
import SliderInputWrapper from './SliderInputWrapper';
import SliderWrapper from './SliderWrapper';
import StyledSlider from './StyledSlider';
import StyledValue from './StyledValue';
import StyledWrapper from './StyledWrapper';
import TrailBar from './TrailBar';

import theme from '$/assets/theme';

export interface ISliderProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'name'> {
  name: string;
  value: number | '';
  min?: number;
  max?: number;
  numberInput?: boolean;
  onChange: (value: string | number) => void;
  error?: boolean | FieldError;
  minMaxFormat?: (data: number) => string | number;
  errorMessage?: InputProps['errorMessage'];
}

// Used to round value on the slider
const __NUMBER_MULTIPLIER__ = 100;

const Slider = React.forwardRef(
  (
    {
      onChange,
      value,
      minMaxFormat = (value) => value?.toFixed(1),
      step = 1,
      ...props
    }: ISliderProps,
    sliderRef: Ref<HTMLInputElement>
  ) => {
    const ref = useRef(null);
    const [textInputVal, setTextInputVal] = useState(value as string | number);
    const [val, setVal] = useState(value as number | string);

    useClickAway(ref, () => {
      setTextInputVal(val);
    });

    const formatValue = (val: string | number): number => {
      if (val === '' || props.disabled) {
        // @ts-ignore
        return '';
      }
      const value = Number(val);
      let newValue = value;

      if (value < props.min!) {
        newValue = props.min!;
      }
      if (value > props.max!) {
        newValue = props.max!;
      }

      const intStep = Number(step);
      if (newValue % intStep !== 0) {
        newValue = Math.round(newValue / intStep) * intStep;
        if (newValue > props.max!) {
          newValue = newValue - Number(step);
        }
      }

      return newValue;
    };

    useEffect(() => {
      const formatted = formatValue(textInputVal);
      if (formatted !== undefined) {
        setVal(formatted);
      }
    }, [textInputVal]);

    useDebounce(
      () => {
        onChange(val);
      },
      200,
      [val]
    );

    useEffect(() => {
      if (Number(value) > Number(props.max)) {
        const newNumber = Number(formatValue(props.max!));
        setVal(Number(newNumber));
        setTextInputVal(Number(newNumber));
        onChange(Number(newNumber));
      }
    }, [props.max]);

    useEffect(() => {
      if (value !== val) {
        setVal(value);
        setTextInputVal(value);
      }
    }, [value]);

    const handleTextValueChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>): void => {
        setTextInputVal(e.currentTarget.value);
      },
      []
    );

    const formattedMax = useMemo(() => {
      return Number(formatValue(props.max!));
    }, [props.max]);

    const handleValueChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>): void => {
        const value = Number(e.currentTarget.value) / __NUMBER_MULTIPLIER__;
        if (step && typeof step === 'number') {
          const intStep = Number(step);
          let toSet = value;
          if (value % intStep !== 0) {
            toSet = Math.round(value / intStep) * intStep;
          }
          toSet = formatValue(toSet);
          switch (step) {
            case 0.1: {
              toSet = Number(toSet.toFixed(1));
              break;
            }
            case 1: {
              toSet = Math.round(toSet);
              break;
            }
          }
          setTextInputVal(toSet);
          setVal(toSet);
          return;
        }

        setTextInputVal(formatValue(value));
        setVal(formatValue(value));
      },
      [props.min, props.max]
    );

    if (props.numberInput) {
      return (
        <SliderContainer {...props}>
          <SliderWrapper disabled={props.max < props.min}>
            <BackgroundBar />
            <TrailBar
              width={
                val !== ''
                  ? (Math.min(Number(val), formattedMax) - props.min) /
                    (formattedMax - props.min)
                  : 0
              }
            />
            <StyledSlider
              type="range"
              ref={sliderRef}
              {...props}
              min={props.min! * __NUMBER_MULTIPLIER__}
              max={formattedMax * __NUMBER_MULTIPLIER__}
              onChange={handleValueChange}
              value={Number(val) * __NUMBER_MULTIPLIER__ ?? ''}
            />
            <StyledWrapper>
              <StyledValue>{minMaxFormat(props.min!)}</StyledValue>
              <StyledValue>
                {minMaxFormat(Number(formatValue(props.max!)))}
              </StyledValue>
            </StyledWrapper>
          </SliderWrapper>
          <SliderInputWrapper disabled={props.max! < props.min!}>
            <TextInput
              type="number"
              step={step || 0.1}
              {...props}
              disableError
              onChange={handleTextValueChange}
              value={
                typeof textInputVal === 'number'
                  ? minMaxFormat(textInputVal)
                  : textInputVal
              }
              style={{
                paddingLeft: 0,
                textAlign: 'center',
              }}
              ref={ref}
            />
          </SliderInputWrapper>
          <MessageContainer
            show={!props.disabled && props.error}
            style={{ paddingTop: '8px' }}
          >
            {!props.disabled && props.error && props.errorMessage && (
              <ErrorMessage
                color={
                  props.max < props.min ? theme.colors.functionalRed : null
                }
              >
                {props.errorMessage}
              </ErrorMessage>
            )}
          </MessageContainer>
        </SliderContainer>
      );
    }

    return (
      <SliderContainer>
        <StyledSlider
          type="range"
          ref={sliderRef}
          {...props}
          onChange={handleValueChange}
          value={val}
        />
        <MessageContainer
          show={!props.disabled && props.error}
          style={{ paddingTop: '8px' }}
        >
          {!props.disabled && props.error && props.errorMessage && (
            <ErrorMessage
              color={props.max < props.min ? theme.colors.functionalRed : null}
            >
              {props.errorMessage}
            </ErrorMessage>
          )}
        </MessageContainer>
      </SliderContainer>
    );
  }
);

const defaultProps = {};

Slider.defaultProps = defaultProps;

export default Slider;
